import type { MatchedTerm, Term } from '../lib/types';
import { Badge } from './ui';

type TermListProps = {
  title: string;
  headingId: string;
  terms: (Term | MatchedTerm)[];
  emptyLabel: string;
  tone: 'matched' | 'missing';
};

/** Pill-chip list of job terms, grouped as matched or missing. */
export function TermList({ title, headingId, terms, emptyLabel, tone }: TermListProps) {
  const chipTone = tone === 'matched' ? 'verified' : 'gap';

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-lg border border-line bg-surface p-4 shadow-card sm:p-5"
    >
      <div className="flex items-center justify-between gap-2">
        <h3
          id={headingId}
          className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-soft"
        >
          {title}
        </h3>
        <Badge tone={chipTone}>{terms.length}</Badge>
      </div>

      {terms.length === 0 ? (
        <p className="mt-3 text-sm text-ink-faint">{emptyLabel}</p>
      ) : (
        <ul className="mt-3 flex flex-wrap gap-2">
          {terms.map((term) => {
            const count = 'resumeCount' in term ? term.resumeCount : term.count;
            return (
              <li key={term.normalized}>
                <span
                  className={[
                    'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-sm',
                    tone === 'matched'
                      ? 'border-verified/25 bg-verified-soft text-verified'
                      : 'border-gap/25 bg-gap-soft text-gap',
                  ].join(' ')}
                >
                  <span className="font-medium">{term.display}</span>
                  <span className="text-xs opacity-70" aria-label={`appears ${count} times`}>
                    ×{count}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
